// Pages/login/passwordReset.tsx
import React, { useState } from "react";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../../services/api";
import backgroundImage from "../../assets/backgrounds/twitterCover.jpg";
import "./auth.css";

// Round 2026-07-27 — pasó a la constante compartida para que no vuelva a
// divergir del backend ni del resto de las pantallas.
import { MIN_PASSWORD_LENGTH } from "../../utils/passwordPolicy";

const PasswordReset: React.FC = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Toggles de ojito, uno por campo. Por defecto OFF (ocultos).
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");

    if (!token) {
      setErrorMsg("El enlace de recuperación es inválido.");
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setErrorMsg(
        `La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`,
      );
      return;
    }
    if (password !== confirmPassword) {
      setErrorMsg("Las contraseñas no coinciden.");
      return;
    }

    setIsSubmitting(true);

    const result = await api.recoveryApi.resetearContrasena(token, password);

    if (result) {
      setSuccessMsg(
        "Contraseña actualizada. Te redirigimos al inicio de sesión...",
      );
      setTimeout(() => navigate("/login"), 2500);
    } else {
      setErrorMsg(
        "No pudimos restablecer la contraseña. El enlace puede haber expirado.",
      );
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div
        className="login-left-panel"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      <div className="login-right-panel">
        <div className="login-container">
          <div className="login-header">
            <h2>Nueva contraseña</h2>
            <p>Elige una contraseña segura para tu cuenta.</p>
          </div>

          {errorMsg && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {errorMsg}
            </div>
          )}

          {successMsg && (
            <div className="success-message">
              <span className="success-icon">✅</span>
              {successMsg}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="password">Nueva contraseña</label>
              <div className="input-container">
                <span className="input-icon">🔑</span>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  className="has-toggle"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={`Mínimo ${MIN_PASSWORD_LENGTH} caracteres`}
                  disabled={isSubmitting || !!successMsg}
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={
                    showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                  }
                  title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmar contraseña</label>
              <div className="input-container">
                <span className="input-icon">🔑</span>
                <input
                  type={showConfirm ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  className="has-toggle"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite la contraseña"
                  disabled={isSubmitting || !!successMsg}
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirm((v) => !v)}
                  tabIndex={-1}
                  aria-label={
                    showConfirm ? "Ocultar contraseña" : "Mostrar contraseña"
                  }
                  title={showConfirm ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showConfirm ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={isSubmitting || !!successMsg}
            >
              {isSubmitting ? (
                <span className="loading-container">
                  <div className="spinner-icon"></div>
                  <span className="loading-text">Guardando...</span>
                </span>
              ) : (
                "Guardar nueva contraseña"
              )}
            </button>
          </form>

          <div className="auth-back-link-wrapper">
            <Link to="/login" className="auth-back-link">
              ← Volver al inicio de sesión
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
